import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const MONTH_NAMES = ['enero','febrero','marzo','abril','mayo','junio',
                     'julio','agosto','septiembre','octubre','noviembre','diciembre'];

function formatFecha(fechaStr) {
  const [y, m, d] = fechaStr.split('-').map(Number);
  return `${d} de ${MONTH_NAMES[m - 1]} de ${y}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function CancelPage() {
  const { token } = useParams();

  const [turno, setTurno]         = useState(null);
  const [tenant, setTenant]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);
  const [step, setStep]           = useState('confirm'); // 'confirm' | 'cancelling' | 'done' | 'already'
  const [error, setError]         = useState('');

  useEffect(() => {
    async function load() {
      if (!UUID_RE.test(token)) { setLoading(false); setNotFound(true); return; }
      // RPC SECURITY DEFINER: accede por cancel_token sin exponer toda la tabla
      const { data } = await supabase
        .rpc('get_turno_by_cancel_token', { p_token: token })
        .single();

      setLoading(false);
      if (!data) { setNotFound(true); return; }

      setTurno(data);
      setTenant({ nombre: data.tenant_nombre, emoji: data.tenant_emoji, slug: data.tenant_slug });

      if (data.estado === 'cancelado') setStep('already');
    }
    load();
  }, [token]);

  const handleCancel = async () => {
    setStep('cancelling');
    setError('');

    // RPC SECURITY DEFINER: cancela el turno usando solo el token
    const { error: dbErr } = await supabase
      .rpc('cancel_turno_by_token', { p_token: token });

    if (dbErr) {
      setError('Algo salió mal. Intentá de nuevo.');
      setStep('confirm');
      return;
    }
    setStep('done');
  };

  if (loading) return (
    <div style={{ textAlign:'center', padding:'6rem', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
      Cargando...
    </div>
  );

  if (notFound) return (
    <div style={{ textAlign:'center', padding:'6rem 2rem', fontFamily:'var(--font-body)' }}>
      <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🔍</div>
      <h2 style={{ fontFamily:'var(--font-head)', fontWeight:800, marginBottom:'.5rem', color:'var(--text-primary)' }}>
        Link inválido
      </h2>
      <p style={{ color:'var(--text-secondary)', marginBottom:'2rem' }}>
        Este link de cancelación no existe o ya expiró.
      </p>
      <Link to="/" style={{ color:'var(--green-light)', fontWeight:600 }}>Volver al inicio</Link>
    </div>
  );

  return (
    <div style={{ maxWidth:480, margin:'0 auto', padding:'48px 24px', fontFamily:'var(--font-body)' }}>

      {/* CARD PRINCIPAL */}
      <div style={{
        background:'var(--surface)', border:'1px solid var(--border)',
        borderRadius:'var(--r-xl)', padding:'32px 28px',
        boxShadow:'var(--shadow)',
      }}>

        {/* HEADER DEL NEGOCIO */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24, paddingBottom:24, borderBottom:'1px solid var(--border)' }}>
          <div style={{
            width:44, height:44, borderRadius:'var(--r-md)',
            background:'var(--green-bg)', border:'1px solid var(--green-border)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:22, flexShrink:0,
          }}>
            {tenant?.emoji || '📅'}
          </div>
          <div>
            <div style={{ fontFamily:'var(--font-head)', fontWeight:700, color:'var(--text-primary)', fontSize:'1rem' }}>
              {tenant?.nombre}
            </div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>Cancelación de turno</div>
          </div>
        </div>

        {/* DETALLES DEL TURNO */}
        <div style={{
          background:'var(--bg3)', borderRadius:'var(--r-lg)', padding:'16px 20px', marginBottom:24,
        }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:12 }}>
            DATOS DEL TURNO
          </div>
          <div style={{ display:'grid', gap:8 }}>
            <Row label="Fecha"   value={formatFecha(turno.fecha)} />
            <Row label="Horario" value={turno.hora} />
            <Row label="Cliente" value={turno.cliente_nombre} />
          </div>
        </div>

        {/* ESTADOS */}

        {step === 'already' && (
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ fontSize:'2rem', marginBottom:12 }}>✅</div>
            <div style={{ fontFamily:'var(--font-head)', fontWeight:700, color:'var(--text-primary)', marginBottom:8 }}>
              Turno ya cancelado
            </div>
            <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
              Este turno fue cancelado previamente.
            </div>
          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign:'center', padding:'8px 0' }}>
            <div style={{ fontSize:'2rem', marginBottom:12 }}>✅</div>
            <div style={{ fontFamily:'var(--font-head)', fontWeight:700, color:'var(--text-primary)', marginBottom:8 }}>
              Turno cancelado
            </div>
            <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
              Tu turno fue cancelado correctamente.
            </div>
          </div>
        )}

        {(step === 'confirm' || step === 'cancelling') && (
          <>
            <div style={{ fontSize:14, color:'var(--text-secondary)', marginBottom:20, lineHeight:1.6 }}>
              ¿Confirmás que querés cancelar este turno?
            </div>
            {error && (
              <p style={{ color:'#f87171', fontSize:13, marginBottom:12 }}>{error}</p>
            )}
            <div style={{ display:'flex', gap:10 }}>
              <button
                onClick={handleCancel}
                disabled={step === 'cancelling'}
                style={{
                  flex:1, padding:'12px', borderRadius:'var(--r-full)',
                  background:'#dc2626', color:'#fff', border:'none',
                  fontFamily:'var(--font-body)', fontSize:14, fontWeight:600,
                  cursor: step === 'cancelling' ? 'default' : 'pointer',
                  opacity: step === 'cancelling' ? .7 : 1, transition:'all .2s',
                }}
              >
                {step === 'cancelling' ? 'Cancelando...' : 'Sí, cancelar turno'}
              </button>
              <Link
                to={`/${tenant?.slug || ''}`}
                style={{
                  padding:'12px 20px', borderRadius:'var(--r-full)',
                  background:'transparent', border:'1px solid var(--border)',
                  color:'var(--text-secondary)', fontFamily:'var(--font-body)', fontSize:13,
                  textDecoration:'none', display:'flex', alignItems:'center',
                }}
              >
                Volver
              </Link>
            </div>
          </>
        )}

      </div>

      <div style={{ textAlign:'center', marginTop:28 }}>
        <span style={{ fontSize:12, color:'var(--text-muted)' }}>
          Agenda gestionada con{' '}
          <Link to="/" style={{ color:'var(--green-light)', fontWeight:600, textDecoration:'none' }}>
            turnokey
          </Link>
        </span>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:600 }}>{label}</span>
      <span style={{ fontSize:14, color:'var(--text-primary)', fontWeight:500 }}>{value}</span>
    </div>
  );
}
